import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { uploadFile } from '../api/files';
import { extractErrorMessage } from '../api/client';
import { storage } from '../lib/storage';
import { useAuthStore } from '../store/auth.store';

const ACCEPT = ['image/jpeg', 'image/png', 'application/pdf'];

export function useUploadFlow() {
  const userGUID = useAuthStore((s) => s.user?.guid);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [progress, setProgress] = useState(0);
  const [activeFile, setActiveFile] = useState<File | null>(null);

  const mutation = useMutation({
    mutationFn: async (file: File) => {
      if (!userGUID) throw new Error('Нет сессии');
      if (!ACCEPT.includes(file.type)) {
        throw new Error('Поддерживаются только JPG, PNG и PDF');
      }
      setActiveFile(file);
      setProgress(0);
      return uploadFile(userGUID, file, {
        onUploadProgress: (e) => {
          if (!e.total) return;
          setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
    },
    onSuccess: (payload) => {
      queryClient.invalidateQueries({ queryKey: ['history', userGUID] });
      // Для PDF сохраняем общее число страниц — на странице выбора нет другого
      // способа его получить (API не отдаёт список страниц оригинала после
      // первого ответа).
      if (payload.file.status === 'SELECT_PAGES' && payload.file.pages) {
        storage.setPendingSelectPageCount(
          payload.file.id,
          payload.file.pages.length,
        );
      }
      navigate(`/files/${payload.file.id}`);
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'Не удалось загрузить файл'));
    },
    onSettled: () => {
      setActiveFile(null);
      setProgress(0);
    },
  });

  const upload = useCallback(
    (file: File) => {
      mutation.mutate(file);
    },
    [mutation],
  );

  return {
    upload,
    isUploading: mutation.isPending,
    progress,
    activeFile,
    error: mutation.error,
  };
}
